import MarkdownIt from "markdown-it";
import * as Y from "yjs";
const YJS_FRAGMENT = "prosemirror";
const MAX_HEADING_LEVEL = 3;
const TASK_PREFIX = /^\[([ xX])\]\s+/;
const markdown = new MarkdownIt({ html: false, linkify: true });
function paragraph(content = []) {
    return content.length > 0
        ? { type: "paragraph", content }
        : { type: "paragraph" };
}
// ── Inline ───────────────────────────────────────────────────────────────
function sameMarks(a = [], b = []) {
    return (a.length === b.length &&
        a.every((mark, index) => mark.type === b[index]?.type &&
            JSON.stringify(mark.attrs ?? {}) ===
                JSON.stringify(b[index]?.attrs ?? {})));
}
function pushText(nodes, text, marks) {
    if (!text)
        return;
    const previous = nodes[nodes.length - 1];
    if (previous?.type === "text" && sameMarks(previous.marks, marks)) {
        previous.text += text;
        return;
    }
    nodes.push(marks.length > 0
        ? { type: "text", text, marks: marks.map((mark) => ({ ...mark })) }
        : { type: "text", text });
}
/** Inline tokens → text/hardBreak/image nodes. Images are returned inline
 *  here and lifted to block level by `liftImages`. */
function parseInline(tokens = []) {
    const nodes = [];
    const marks = [];
    const removeMark = (type) => {
        const index = marks.map((mark) => mark.type).lastIndexOf(type);
        if (index >= 0)
            marks.splice(index, 1);
    };
    for (const token of tokens) {
        switch (token.type) {
            case "text":
            case "html_inline":
                pushText(nodes, token.content, marks);
                break;
            case "softbreak":
                pushText(nodes, " ", marks);
                break;
            case "hardbreak":
                nodes.push({ type: "hardBreak" });
                break;
            case "code_inline":
                // Tiptap's code mark excludes every other mark.
                pushText(nodes, token.content, [{ type: "code" }]);
                break;
            case "strong_open":
                marks.push({ type: "bold" });
                break;
            case "strong_close":
                removeMark("bold");
                break;
            case "em_open":
                marks.push({ type: "italic" });
                break;
            case "em_close":
                removeMark("italic");
                break;
            case "s_open":
                marks.push({ type: "strike" });
                break;
            case "s_close":
                removeMark("strike");
                break;
            case "link_open":
                marks.push({ type: "link", attrs: { href: token.attrGet("href") } });
                break;
            case "link_close":
                removeMark("link");
                break;
            case "image": {
                const attrs = {
                    src: token.attrGet("src"),
                    alt: token.content || null,
                };
                const title = token.attrGet("title");
                if (title)
                    attrs.title = title;
                nodes.push({ type: "image", attrs });
                break;
            }
            default:
                if (token.content)
                    pushText(nodes, token.content, marks);
        }
    }
    return nodes;
}
/** The editor's image node is a block, so split a paragraph around images. */
function liftImages(inline) {
    if (!inline.some((node) => node.type === "image"))
        return [paragraph(inline)];
    const blocks = [];
    let run = [];
    const flush = () => {
        while (run[0]?.type === "hardBreak")
            run.shift();
        while (run[run.length - 1]?.type === "hardBreak")
            run.pop();
        const text = run.map((node) => node.text ?? "").join("");
        if (run.some((node) => node.type !== "text") || text.trim()) {
            blocks.push(paragraph(run));
        }
        run = [];
    };
    for (const node of inline) {
        if (node.type === "image") {
            flush();
            blocks.push(node);
        }
        else {
            run.push(node);
        }
    }
    flush();
    return blocks;
}
/** List and task items must start with a paragraph. */
function ensureLeadingParagraph(content) {
    return content[0]?.type === "paragraph" ? content : [paragraph(), ...content];
}
function parseBlocks(cursor, closeType) {
    const blocks = [];
    while (cursor.index < cursor.tokens.length) {
        const token = cursor.tokens[cursor.index];
        if (!token)
            break;
        if (closeType && token.type === closeType) {
            cursor.index++;
            return blocks;
        }
        cursor.index++;
        switch (token.type) {
            case "paragraph_open": {
                const inline = cursor.tokens[cursor.index];
                cursor.index += 2; // inline + paragraph_close
                blocks.push(...liftImages(parseInline(inline?.children ?? [])));
                break;
            }
            case "heading_open": {
                const inline = cursor.tokens[cursor.index];
                cursor.index += 2; // inline + heading_close
                const level = Math.min(MAX_HEADING_LEVEL, Number(token.tag.slice(1)) || 1);
                const [first, ...rest] = liftImages(parseInline(inline?.children ?? []));
                blocks.push({
                    type: "heading",
                    attrs: { level },
                    ...(first?.type === "paragraph" && first.content
                        ? { content: first.content }
                        : {}),
                });
                if (first && first.type !== "paragraph")
                    blocks.push(first);
                blocks.push(...rest);
                break;
            }
            case "bullet_list_open":
                blocks.push(toTaskList(parseList(cursor, "bullet_list_close")));
                break;
            case "ordered_list_open": {
                const start = Number(token.attrGet("start") ?? 1);
                const list = {
                    type: "orderedList",
                    content: parseList(cursor, "ordered_list_close"),
                };
                if (start !== 1)
                    list.attrs = { start };
                blocks.push(list);
                break;
            }
            case "blockquote_open": {
                const content = parseBlocks(cursor, "blockquote_close");
                blocks.push({
                    type: "blockquote",
                    content: content.length > 0 ? content : [paragraph()],
                });
                break;
            }
            case "fence":
            case "code_block": {
                const text = token.content.replace(/\n$/, "");
                const language = token.info.trim().split(/\s+/)[0] || null;
                blocks.push({
                    type: "codeBlock",
                    ...(language ? { attrs: { language } } : {}),
                    ...(text ? { content: [{ type: "text", text }] } : {}),
                });
                break;
            }
            case "hr":
                blocks.push({ type: "horizontalRule" });
                break;
            case "table_open":
                blocks.push(parseTable(cursor));
                break;
            case "html_block": {
                const text = token.content.trim();
                if (text)
                    blocks.push(paragraph([{ type: "text", text }]));
                break;
            }
            default:
                // Unknown block tokens carry no content of their own.
                break;
        }
    }
    return blocks;
}
function parseList(cursor, closeType) {
    const items = [];
    while (cursor.index < cursor.tokens.length) {
        const token = cursor.tokens[cursor.index++];
        if (!token || token.type === closeType)
            break;
        if (token.type === "list_item_open") {
            items.push({
                type: "listItem",
                content: ensureLeadingParagraph(parseBlocks(cursor, "list_item_close")),
            });
        }
    }
    return items;
}
/** A bullet list whose items all start with `[ ]` or `[x]` is a task list. */
function toTaskList(items) {
    const firstText = (item) => item.content?.[0]?.content?.[0]?.type === "text"
        ? item.content[0].content[0]
        : undefined;
    const isTaskList = items.length > 0 &&
        items.every((item) => TASK_PREFIX.test(firstText(item)?.text ?? ""));
    if (!isTaskList)
        return { type: "bulletList", content: items };
    return {
        type: "taskList",
        content: items.map((item) => {
            const text = firstText(item);
            const match = TASK_PREFIX.exec(text.text ?? "");
            text.text = (text.text ?? "").slice(match[0].length);
            const lead = item.content[0];
            if (!text.text)
                lead.content.shift();
            if (lead.content?.length === 0)
                delete lead.content;
            return {
                type: "taskItem",
                attrs: { checked: match[1] !== " " },
                content: item.content,
            };
        }),
    };
}
function parseTable(cursor) {
    const rows = [];
    let row = null;
    while (cursor.index < cursor.tokens.length) {
        const token = cursor.tokens[cursor.index++];
        if (!token || token.type === "table_close")
            break;
        if (token.type === "tr_open")
            row = [];
        else if (token.type === "tr_close" && row) {
            rows.push({ type: "tableRow", content: row });
            row = null;
        }
        else if ((token.type === "th_open" || token.type === "td_open") && row) {
            const inline = cursor.tokens[cursor.index];
            cursor.index += 2; // inline + th_close/td_close
            const content = liftImages(parseInline(inline?.children ?? []));
            row.push({
                type: token.type === "th_open" ? "tableHeader" : "tableCell",
                content: content.length > 0 ? content : [paragraph()],
            });
        }
    }
    return { type: "table", content: rows };
}
/** Parses markdown into ProseMirror JSON matching the editor's schema. */
export function markdownToProseMirrorJSON(source) {
    const content = parseBlocks({ index: 0, tokens: markdown.parse(source, {}) });
    return { type: "doc", content: content.length > 0 ? content : [paragraph()] };
}
// ── Yjs ──────────────────────────────────────────────────────────────────
function appendNodes(parent, nodes = []) {
    let index = 0;
    while (index < nodes.length) {
        const node = nodes[index];
        if (node.type === "text") {
            // y-prosemirror keeps a run of adjacent text nodes in one XmlText, with
            // each mark stored as a formatting attribute holding the mark's attrs.
            const delta = [];
            while (nodes[index]?.type === "text") {
                const text = nodes[index];
                const attributes = {};
                for (const mark of text.marks ?? []) {
                    attributes[mark.type] = mark.attrs ?? {};
                }
                delta.push({ insert: text.text ?? "", attributes });
                index++;
            }
            const yText = new Y.XmlText();
            parent.insert(parent.length, [yText]);
            yText.applyDelta(delta);
            continue;
        }
        const element = new Y.XmlElement(node.type);
        parent.insert(parent.length, [element]);
        for (const [key, value] of Object.entries(node.attrs ?? {})) {
            if (value !== null && value !== undefined) {
                element.setAttribute(key, value);
            }
        }
        appendNodes(element, node.content);
        index++;
    }
}
/**
 * Replaces the editor content of `doc` with `source` in one transaction.
 * Use this on a document connected to the collaboration server so the change
 * syncs to every open editor.
 */
export function replaceYDocWithMarkdown(doc, source, origin) {
    const fragment = doc.getXmlFragment(YJS_FRAGMENT);
    const json = markdownToProseMirrorJSON(source);
    doc.transact(() => {
        if (fragment.length > 0)
            fragment.delete(0, fragment.length);
        appendNodes(fragment, json.content);
    }, origin);
}
/**
 * Appends `source` to the end of the editor content of `doc` in one
 * transaction, leaving existing content untouched.
 */
export function appendMarkdownToYDoc(doc, source, origin) {
    const fragment = doc.getXmlFragment(YJS_FRAGMENT);
    const json = markdownToProseMirrorJSON(source);
    doc.transact(() => appendNodes(fragment, json.content), origin);
}
/** Builds a fresh Y.Doc holding `source` as editor content. */
export function markdownToYDoc(source) {
    const doc = new Y.Doc();
    replaceYDocWithMarkdown(doc, source);
    return doc;
}
/**
 * Encodes `source` as a Yjs update, e.g. for the initial `documents.data` of
 * a document no editor has opened yet.
 */
export function markdownToYjsUpdate(source) {
    const doc = markdownToYDoc(source);
    try {
        return Y.encodeStateAsUpdate(doc);
    }
    finally {
        doc.destroy();
    }
}
